import { Injectable } from '@angular/core';
import { Observable, throwError, Subject } from 'rxjs';
import { catchError, finalize, tap, map } from 'rxjs/operators';
import { IsLoadingService } from '@service-work/is-loading';
import { DocumentExtractApiService } from './document-extract-api.service';
import { DocumentExtractSchemaService } from './document-extract-schema.service';
import {
  DocumentExtractResponse,
  DocumentExtractError
} from '../models/document-extract/document-extract.model';
import {
  DOCUMENT_EXTRACT_ERRORS,
  LOADING_INDICATORS
} from '../constants/document-extract.constants';

/**
 * DocumentExtractDataService
 * 
 * Business logic layer for document extraction.
 * Sits between components and the API service, handling:
 * - Loading state management
 * - Schema retrieval and validation
 * - Response transformation
 * - Error handling
 * 
 * This service follows the existing architecture pattern seen in:
 * - IncidentsDataService
 * - YardInspectionDataService
 * - ShipmentDataService
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentExtractDataService {
  constructor(
    private readonly apiService: DocumentExtractApiService,
    private readonly schemaService: DocumentExtractSchemaService,
    private readonly isLoadingService: IsLoadingService
  ) {}

  /**
   * Extracts data from a document file
   * 
   * @param file The file to extract data from
   * @param documentType The type of document being extracted
   * @param customSchema Optional custom schema (if not using registry)
   * @param customDropdownOptions Optional custom dropdown options
   * @param customFormatInstructions Optional custom format instructions
   * @returns Observable<DocumentExtractResponse> The extracted data
   */
  extractDocument(
    file: File,
    documentType: string,
    customSchema?: Record<string, string>,
    customDropdownOptions?: Record<string, string[]>,
    customFormatInstructions?: Record<string, string>
  ): Observable<DocumentExtractResponse> {
    // Validate inputs
    if (!file) {
      return throwError(() => this.createError(DOCUMENT_EXTRACT_ERRORS.NO_FILE_PROVIDED));
    }

    if (!documentType && !customSchema) {
      return throwError(() => this.createError(DOCUMENT_EXTRACT_ERRORS.INVALID_DOCUMENT_TYPE));
    }

    // Create loading subject for this operation
    const loadingSubject = new Subject<void>();
    
    // Get schema and dropdown options
    let schema: Record<string, string>;
    let dropdownOptions: Record<string, string[]> | undefined;
    let formatInstructions: Record<string, string> | undefined;

    try {
      if (customSchema) {
        // Use custom schema if provided
        schema = customSchema;
        dropdownOptions = customDropdownOptions;
        formatInstructions = customFormatInstructions;
      } else {
        // Get schema from registry
        const config = this.schemaService.getConfigForDocumentType(documentType);
        schema = config.schema;
        dropdownOptions = config.dropdownOptions;
        formatInstructions = customFormatInstructions; // Allow override even with registry schema
      }
    } catch (error: any) {
      loadingSubject.complete();
      return throwError(() => this.createError(error.message || DOCUMENT_EXTRACT_ERRORS.SCHEMA_NOT_FOUND));
    }

    // Call API service
    const extraction$ = this.apiService.extractDocument(file, schema, documentType, dropdownOptions, formatInstructions).pipe(
      map((response: DocumentExtractResponse) => this.transformResponse(response, documentType)),
      tap({
        next: () => {
          // Success - could emit success event here if needed
        },
        error: (error: DocumentExtractError) => {
          // Error handling already done by API service
        }
      }),
      catchError((error: DocumentExtractError) => {
        return throwError(() => error);
      }),
      finalize(() => {
        loadingSubject.complete();
      })
    );

    // Register loading indicator
    this.isLoadingService.add(loadingSubject.asObservable(), {
      key: LOADING_INDICATORS.EXTRACTING
    });

    return extraction$;
  }

  /**
   * Extracts data from multiple documents
   * 
   * @param files Array of files with their document types
   * @returns Observable<DocumentExtractResponse[]> Array of extraction results
   */
  extractMultipleDocuments(
    files: Array<{ file: File; documentType: string }>
  ): Observable<DocumentExtractResponse[]> {
    const extractions: Observable<DocumentExtractResponse>[] = files.map(({ file, documentType }) =>
      this.extractDocument(file, documentType)
    );

    // Note: This returns an array observable, not combining them
    // Components should use forkJoin or similar if they want to wait for all
    return new Observable(observer => {
      const results: DocumentExtractResponse[] = [];
      let completed = 0;

      extractions.forEach((extraction$, index) => {
        extraction$.subscribe({
          next: (result) => {
            results[index] = result;
            completed++;
            if (completed === extractions.length) {
              observer.next(results);
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
          }
        });
      });
    });
  }

  /**
   * Checks if a document type is supported for extraction
   * 
   * @param documentType The document type to check
   * @returns boolean True if supported, false otherwise
   */
  isDocumentTypeSupported(documentType: string): boolean {
    return this.schemaService.isDocumentTypeSupported(documentType);
  }

  /**
   * Gets all supported document types
   * 
   * @returns string[] Array of supported document type identifiers
   */
  getSupportedDocumentTypes(): string[] {
    return this.schemaService.getAllSupportedDocumentTypes();
  }

  /**
   * Gets display name for a document type
   * 
   * @param documentType The document type identifier
   * @returns string | undefined The display name or undefined
   */
  getDocumentTypeDisplayName(documentType: string): string | undefined {
    return this.schemaService.getDisplayNameForDocumentType(documentType);
  }

  /**
   * Checks the health of the Document Extract API
   * 
   * @returns Observable<any> Health check response
   */
  checkHealth(): Observable<any> {
    const loadingSubject = new Subject<void>();

    const health$ = this.apiService.checkHealth().pipe(
      finalize(() => {
        loadingSubject.complete();
      })
    );

    this.isLoadingService.add(loadingSubject.asObservable(), {
      key: LOADING_INDICATORS.HEALTH_CHECK
    });

    return health$;
  }

  /**
   * Clears the authentication token cache
   * Useful for logout or session end scenarios
   */
  clearAuthCache(): void {
    this.apiService.clearCache();
  }

  /**
   * Transforms the API response to a more usable format for the application
   * 
   * @param response The raw API response
   * @param documentType The document type being extracted
   * @returns DocumentExtractResponse The transformed response
   */
  private transformResponse(
    response: DocumentExtractResponse,
    documentType: string
  ): DocumentExtractResponse {
    // Add document type to response
    const transformed: DocumentExtractResponse = {
      ...response,
      documentType
    };

    // Transform date strings to Date objects if needed
    // This could be expanded based on application needs
    // For now, we return the response as-is with document type added

    return transformed;
  }

  /**
   * Creates a standardized error object
   * 
   * @param message Error message
   * @returns DocumentExtractError The error object
   */
  private createError(message: string): DocumentExtractError {
    return {
      message
    };
  }

  /**
   * Gets the extraction schema for a document type
   * Exposed for components that need to display schema information
   * 
   * @param documentType The document type identifier
   * @returns Record<string, string> The extraction schema
   */
  getSchemaForDocumentType(documentType: string): Record<string, string> {
    return this.schemaService.getSchemaForDocumentType(documentType);
  }

  /**
   * Gets dropdown options for a document type
   * Exposed for components that need to display dropdown options
   * 
   * @param documentType The document type identifier
   * @returns Record<string, any[]> | undefined The dropdown options
   */
  getDropdownOptionsForDocumentType(documentType: string): Record<string, any[]> | undefined {
    return this.schemaService.getDropdownOptionsForDocumentType(documentType);
  }
}

